// In-page anchors (ported from the former site runtime, not in the D7 table): a
// click on a link to an element of the same page is cancelled, its hash is pushed to the
// history once, the window scrolls to the element with an eased animation whose length
// grows with the distance, then the element takes the focus without scrolling. Reduced
// motion makes the scroll instant. A click on a bare href="#" link is cancelled.

import { prefersReducedMotion } from "./transitions.js";

const SELECTORS = {
  link: "a",
  empty: 'a[href="#"]',
};

// The hashes the former runtime handled (others navigate as usual).
const HASH = /^#[a-zA-Z0-9][\w:.-]*$/;

/** Scroll duration in ms for a distance in px (the former runtime's curve). */
function duration(distance) {
  if (prefersReducedMotion()) return 0;
  return 472.143 * Math.log(Math.abs(distance) + 125) - 2000;
}

/** Cubic ease-in-out. */
function ease(t) {
  return t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1;
}

/** Moves the focus to `target` (made focusable for the call) without scrolling. */
function focusWithoutScroll(target) {
  const hadTabindex = target.hasAttribute("tabindex");
  if (!hadTabindex) target.setAttribute("tabindex", "-1");
  target.focus({ preventScroll: true });
  if (!hadTabindex) target.removeAttribute("tabindex");
}

// The running scroll animation, if any: a new click replaces it.
let frame = 0;

function scrollToElement(target) {
  cancelAnimationFrame(frame);
  const start = window.scrollY;
  const end = target.getBoundingClientRect().top + start;
  if (start === end) return;
  const total = duration(end - start);
  const t0 = performance.now();
  const step = () => {
    const progress = total > 0 ? Math.min((performance.now() - t0) / total, 1) : 1;
    window.scrollTo(0, start + (end - start) * ease(progress));
    if (progress < 1) frame = requestAnimationFrame(step);
    else focusWithoutScroll(target);
  };
  frame = requestAnimationFrame(step);
}

function onClick(event) {
  const link = event.target instanceof Element ? event.target.closest(SELECTORS.link) : null;
  if (!link) return;
  if (link.matches(SELECTORS.empty)) {
    event.preventDefault();
    return;
  }
  const samePage = link.host + link.pathname === location.host + location.pathname;
  if (!samePage || !HASH.test(link.hash)) return;
  const target = document.getElementById(link.hash.slice(1));
  if (!target) return;
  event.preventDefault();
  if (location.hash !== link.hash && history.state?.hash !== link.hash) {
    history.pushState({ hash: link.hash }, "", link.hash);
  }
  scrollToElement(target);
}

export function initAnchors() {
  document.addEventListener("click", onClick);
}
